import { DateTime } from "luxon";
import Select from "react-select";

import {
  CaseReportSignature,
  User,
  LocationCode,
  UserCredentials,
} from "pwapi";

import "./SignatureField.css";

import { LabSettings } from "pwapi/custom/LabSettings";

import { isPromedicaCert } from "shared/utils/labUtils";

import CustomInput from "components/customInputs/CustomInput";

import {
  getLocationStringForSignature,
  PW_DATETIME_WITH_ZONE,
  sameAddress,
} from "./utils";

type SignatureFieldProps = {
  signatures: CaseReportSignature[];
  user?: User | UserCredentials;
  value?: CaseReportSignature;
  onSelect: Function;
  locationCode?: LocationCode;
  pcCliaDifferentFromTcClia?: boolean;
  shouldShowDigitizationDisclaimer?: boolean;
  scannerCompanies?: (string | undefined)[];
  labSettings?: LabSettings;
};

const formatSignatureOption = ({
  label,
  user,
  locationCode,
  pcCliaDifferentFromTcClia,
  shouldShowDigitizationDisclaimer,
  scannerCompanies,
  labSettings,
}: {
  label: CaseReportSignature;
  user?: User | UserCredentials;
  locationCode?: LocationCode;
  pcCliaDifferentFromTcClia?: boolean;
  shouldShowDigitizationDisclaimer?: boolean;
  scannerCompanies?: (string | undefined)[];
  labSettings?: LabSettings;
}) => {
  const selectedSignature = label;
  const shouldShowLabDirector =
    (labSettings?.lab_director_on_report?.active ||
      selectedSignature?.clia_certificate?.lab?.settings?.lab_director_on_report
        ?.active) &&
    selectedSignature?.clia_certificate?.lab_director;

  return (
    <div className="SignatureField SignatureField--vertical">
      <span>
        {user?.first_name} {user?.last_name}
        {user?.userprofile?.credentials
          ? `, ${user.userprofile?.credentials}`
          : ""}
      </span>
      {user?.userprofile.title && <span>{user?.userprofile.title}</span>}
      {label.clia_certificate?.location?.timezone && (
        <span>
          Final report electronically signed on{" "}
          {DateTime.local({
            zone: label.clia_certificate.location.timezone,
          }).toLocaleString({
            ...PW_DATETIME_WITH_ZONE,
            timeZoneName: "long",
          })}
        </span>
      )}

      <span>
        <SignatureLine
          selectedSignature={label}
          locationCode={locationCode}
          pcCliaDifferentFromTcClia={pcCliaDifferentFromTcClia}
          shouldShowDigitizationDisclaimer={shouldShowDigitizationDisclaimer}
          scannerCompanies={scannerCompanies}
        />{" "}
        CLIA&nbsp;#&nbsp;{selectedSignature.clia_certificate?.number}
        {shouldShowLabDirector && pcCliaDifferentFromTcClia && (
          <>
            , Laboratory Director:{" "}
            {selectedSignature.clia_certificate.lab_director.first_name}{" "}
            {selectedSignature.clia_certificate.lab_director.last_name}
            {selectedSignature.clia_certificate.lab_director.credentials && (
              <>
                , {selectedSignature.clia_certificate.lab_director.credentials}
              </>
            )}
          </>
        )}
      </span>
    </div>
  );
};

export const SignatureLine = ({
  selectedSignature,
  locationCode,
  pcCliaDifferentFromTcClia,
  shouldShowDigitizationDisclaimer,
  scannerCompanies,
}: {
  selectedSignature: CaseReportSignature;
  locationCode?: LocationCode;
  pcCliaDifferentFromTcClia?: boolean;
  shouldShowDigitizationDisclaimer?: boolean;
  scannerCompanies?: (string | undefined)[];
}) => {
  const shouldShowLocationCode =
    !!locationCode?.code &&
    !sameAddress(
      selectedSignature?.clia_certificate?.location,
      locationCode.location
    );

  if (
    !(
      shouldShowLocationCode ||
      pcCliaDifferentFromTcClia ||
      shouldShowDigitizationDisclaimer
    )
  )
    return null;

  return (
    <>
      The professional component was performed{" "}
      {shouldShowLocationCode || pcCliaDifferentFromTcClia ? "at" : ""}{" "}
      {pcCliaDifferentFromTcClia && (
        <>
          {!isPromedicaCert(selectedSignature.clia_certificate) && (
            <>
              {selectedSignature.clia_certificate.name}
              {!selectedSignature.clia_certificate.suppress_location && ", "}
            </>
          )}
          {!selectedSignature.clia_certificate.suppress_location &&
            getLocationStringForSignature(selectedSignature)}
        </>
      )}
      {shouldShowLocationCode && (
        <>
          {pcCliaDifferentFromTcClia ? ", " : ""} Remote Location{" "}
          {locationCode.code}
        </>
      )}
      {shouldShowDigitizationDisclaimer && (
        <>
          {" "}
          using the PathologyWatch DOT.{" "}
          {!!scannerCompanies?.length && (
            <>and {scannerCompanies?.join(" and ")} </>
          )}
          system
          {!!scannerCompanies?.length ? "s" : ""}
        </>
      )}
      .
    </>
  );
};

const SignatureField = ({
  signatures,
  onSelect,
  user,
  value,
  locationCode,
  pcCliaDifferentFromTcClia,
  shouldShowDigitizationDisclaimer,
  scannerCompanies,
  labSettings,
}: SignatureFieldProps) => {
  return (
    <CustomInput label="Signature">
      <Select
        value={
          value
            ? {
                value: value.id,
                label: value,
                user,
                locationCode,
                pcCliaDifferentFromTcClia,
                shouldShowDigitizationDisclaimer,
                scannerCompanies,
                labSettings,
              }
            : undefined
        }
        formatOptionLabel={formatSignatureOption}
        options={signatures?.map((sig) => ({
          value: sig.id,
          label: sig,
          locationCode,
          user,
          pcCliaDifferentFromTcClia,
          shouldShowDigitizationDisclaimer,
          scannerCompanies,
          labSettings,
        }))}
        onChange={(newValue) => {
          onSelect(newValue);
        }}
        className="SignatureField__select"
        placeholder="Select a signature"
      />
    </CustomInput>
  );
};

export default SignatureField;
